import React, {
  useState,
  useEffect
} from "react";

import axios from "axios";

import "./Thrive.css";

import VisionNavbar from "./VisionNavbar";

import PerformanceGraphs from "./PerformanceGraphs";


const Thrive = () => {


  /* =====================================================
     HIDE MAIN NAVBAR / FOOTER
  ===================================================== */

  useEffect(() => {

    const navbar =
      document.querySelector(
        "header, .navbar"
      );

    const footer =
      document.querySelector(
        "footer"
      );


    if (navbar)
      navbar.style.display = "none";

    if (footer)
      footer.style.display = "none";


    return () => {

      if (navbar)
        navbar.style.display = "";

      if (footer)
        footer.style.display = "";

    };

  }, []);


  /* =====================================================
     EMAIL
  ===================================================== */

  const email =
    localStorage.getItem(
      "user_email"
    );


  /* =====================================================
     STATES
  ===================================================== */

  const [
    dashboard,
    setDashboard
  ] = useState(null);


  const [
    journey,
    setJourney
  ] = useState(null);


  const [
    history,
    setHistory
  ] = useState({});


  const [
    loading,
    setLoading
  ] = useState(true);


  /* =====================================================
     LEVEL 4 MANUAL TASKS
  ===================================================== */

  const [
    reduceGlasses,
    setReduceGlasses
  ] = useState(false);


  const [
    helpMember,
    setHelpMember
  ] = useState(false);


  /* =====================================================
     DAILY ROUTINE
  ===================================================== */

  const [
    eyeExercise,
    setEyeExercise
  ] = useState(false);


  const [
    visualMemory,
    setVisualMemory
  ] = useState(false);


  const [
    outdoorLight,
    setOutdoorLight
  ] = useState(false);


  const [
    blinkRelaxation,
    setBlinkRelaxation
  ] = useState(false);


  /* =====================================================
     FETCH DATA
  ===================================================== */

  useEffect(() => {

    if (email) {

      fetchData();

    }

  }, [email]);


  const fetchData =
    async () => {

      try {

        setLoading(true);


        const [

          dashRes,

          journeyRes,

          historyRes

        ] = await Promise.all([

          axios.get(
            `/api/dashboard/${email}`
          ),

          axios.get(
            `/api/journey/${email}`
          ),

          axios.get(
            `/api/admin/player-history/${email}`
          )

        ]);


        setDashboard(
          dashRes.data
        );


        /*
          Backend response:

          {
            success: true,
            journey: {...},
            level1Completed,
            level2Completed,
            level3Completed,
            level4Completed
          }

          So we store the complete
          response.
        */

        setJourney(
          journeyRes.data
        );


        setHistory(
          historyRes.data
        );

      }

      catch (err) {

        console.error(
          "Thrive fetch error:",
          err
        );

      }

      finally {

        setLoading(false);

      }

    };


  /* =====================================================
     LOAD JOURNEY VALUES
  ===================================================== */

  useEffect(() => {

    if (!journey)
      return;


    const journeyData =
      journey.journey ||
      journey;


    setReduceGlasses(

      journeyData?.level4
        ?.reduceGlasses ||
      false

    );


    setHelpMember(

      journeyData?.level4
        ?.helpMember ||
      false

    );


    setEyeExercise(

      journeyData?.level4
        ?.routine
        ?.eyeExercise ||
      false

    );


    setVisualMemory(

      journeyData?.level4
        ?.routine
        ?.visualMemory ||
      false

    );


    setOutdoorLight(

      journeyData?.level4
        ?.routine
        ?.outdoorLight ||
      false

    );


    setBlinkRelaxation(

      journeyData?.level4
        ?.routine
        ?.blinkRelaxation ||
      false

    );

  }, [journey]);


  /* =====================================================
     UPDATE LEVEL 4 TASK
  ===================================================== */

  const updateLevel4Task =
    async (
      field,
      value
    ) => {

      try {

        await axios.patch(

          "/api/journey/update",

          {

            email,

            section:
              "level4",

            field,

            value

          }

        );


        /*
          Refresh backend data
          after update.
        */

        await fetchData();

      }

      catch (err) {

        console.error(
          "Level 4 update error:",
          err
        );

      }

    };


  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {

    return (

      <div
        className="thrive-loading"
      >

        Loading...

      </div>

    );

  }


  /* =====================================================
     LEVEL
  ===================================================== */

  const level = 4;

  const currentLevel =
    "Thriver";


  const dailyStreak =
    dashboard?.dailyStreak ||
    0;


  /* =====================================================
     JOURNEY DATA
  ===================================================== */

  const journeyData =
    journey?.journey ||
    journey ||
    {};


  /* =====================================================
     LEVEL 3 COMPLETION
  ===================================================== */

  /*
    Level 3 is considered complete when:

    1. At least one metric improved
    2. 60 Day Evaluation completed
    3. Progress Story completed
    4. Daily Practice Streak completed
  */

  const level3Completed =

    (
      journeyData
        ?.level3
        ?.improvedMetrics
        ?.length ||
      0
    ) >= 1 &&

    journeyData
      ?.level3
      ?.evaluation60Days === true &&

    journeyData
      ?.level3
      ?.progressStory === true &&

    journeyData
      ?.level3
      ?.dailyPracticeStreak === true;


  /* =====================================================
     IMPROVED METRICS
  ===================================================== */

  const improvedMetrics =

    journeyData
      ?.level3
      ?.improvedMetrics ||
    [];


  const improvedCount =
    improvedMetrics.length;


  const metricsProgress =

    Math.min(

      improvedCount,

      3

    );


  /* =====================================================
     LEVEL 4 REQUIREMENTS
  ===================================================== */

  const evaluation90Days =

    journeyData
      ?.level4
      ?.evaluation90Days ||
    false;


  const tasks = [

    {

      label:
        "Improve 3 Metrics",

      completed:
        metricsProgress >= 3,

      manual:
        false

    },

    {

      label:
        "90 Day Evaluation",

      completed:
        evaluation90Days,

      manual:
        false

    },

    {

      label:
        "Reduced Glasses Dependency",

      completed:
        reduceGlasses,

      manual:
        true

    },

    {

      label:
        "Help One Member",

      completed:
        helpMember,

      manual:
        true

    }

  ];


  /* =====================================================
     OVERALL TASK PROGRESS
  ===================================================== */

  const completedTasks =

    tasks.filter(

      task =>
        task.completed

    ).length;


  const progressPercent =

    Math.round(

      (

        completedTasks /

        tasks.length

      ) * 100

    );


  /* =====================================================
     PERSONAL BESTS
  ===================================================== */

  const personalRecords = [

    {

      title:
        "Visual Acuity",

      value:

        dashboard?.tests
          ?.find(

            t =>
              t.name ===
              "Visual Acuity"

          )?.score ||
        "-"

    },

    {

      title:
        "Contrast",

      value:

        dashboard?.tests
          ?.find(

            t =>
              t.name ===
              "Contrast Sensitivity"

          )?.score ||
        "-"

    },

    {

      title:
        "Working Memory",

      value:

        dashboard?.tests
          ?.find(

            t =>
              t.name ===
              "Working Memory"

          )?.score ||
        "-"

    },

    {

      title:
        "Search Span",

      value:

        dashboard?.tests
          ?.find(

            t =>
              t.name ===
              "Search Span"

          )?.score ||
        "-"

    },

    {

      title:
        "Anticipation",

      value:

        dashboard?.tests
          ?.find(

            t =>
              t.name ===
              "Anticipation"

          )?.score ||
        "-"

    },

    {

      title:
        "Short-Term Memory",

      value:

        dashboard?.tests
          ?.find(

            t =>
              t.name ===
              "Short Term Memory"

          )?.score ||
        "-"

    }

  ];


  /* =====================================================
     DAILY VISION ROUTINE
  ===================================================== */

  const routineTasks = [

    {

      label:
        "Eye Exercise",

      checked:
        eyeExercise,

      setter:
        setEyeExercise,

      field:
        "eyeExercise"

    },

    {

      label:
        "Visual Memory Training",

      checked:
        visualMemory,

      setter:
        setVisualMemory,

      field:
        "visualMemory"

    },

    {

      label:
        "Outdoor Light Exposure",

      checked:
        outdoorLight,

      setter:
        setOutdoorLight,

      field:
        "outdoorLight"

    },

    {

      label:
        "Blink & Eye Relaxation",

      checked:
        blinkRelaxation,

      setter:
        setBlinkRelaxation,

      field:
        "blinkRelaxation"

    }

  ];


  const completedRoutine =

    routineTasks.filter(

      item =>
        item.checked

    ).length;


  /* =====================================================
     RENDER
  ===================================================== */

  return (

    <div className="thrive-page">


      <VisionNavbar
        active="thrive"
      />


      {/* =================================================
          HERO
      ================================================= */}

      <section
        className="thrive-hero"
      >


        {/* =================================================
            LEFT
        ================================================= */}

        <div
          className="thrive-left"
        >


          


          <h1>

            Thrive
            Ecosystem

          </h1>


          <p>

            Maintain your vision,
            track your personal
            records and continue
            improving every day.

          </p>


          {/* =================================================
              STATUS CARD
          ================================================= */}

          <div
            className="thrive-status-card"
          >


            <span
              className="status-label"
            >

              CURRENT STATUS

            </span>


            <h2>

              Level 4 Thriver

            </h2>


            <p>

              Complete every
              requirement to
              maintain your
              highest level.

            </p>


            {/* =================================================
                REQUIREMENTS
            ================================================= */}

            <div
              className="requirements"
            >


              {

                tasks.map(

                  (
                    task,
                    index
                  ) => (


                    <div

                      key={index}

                      className={

                        `requirement-item ${
                          task.manual &&
                          !level3Completed
                            ? "task-locked"
                            : ""
                        }`

                      }

                    >


                      <div
                        className="requirement-left"
                      >


                        {/* =================================================
                            MANUAL TASKS
                        ================================================= */}

                        {

                          task.manual

                          ?

                          (

                            <>

                              {

                                !level3Completed && (

                                  <span
                                    className="lock-icon"
                                  >

                                    🔒

                                  </span>

                                )

                              }


                              <input

                                type="checkbox"

                                checked={

                                  task.label ===
                                  "Reduced Glasses Dependency"

                                    ?

                                    reduceGlasses

                                    :

                                    helpMember

                                }

                                disabled={
                                  !level3Completed
                                }

                                onChange={

                                  (e) => {

                                    if (
                                      !level3Completed
                                    ) {

                                      return;

                                    }


                                    if (

                                      task.label ===
                                      "Reduced Glasses Dependency"

                                    ) {

                                      setReduceGlasses(

                                        e.target.checked

                                      );


                                      updateLevel4Task(

                                        "reduceGlasses",

                                        e.target.checked

                                      );

                                    }


                                    if (

                                      task.label ===
                                      "Help One Member"

                                    ) {

                                      setHelpMember(

                                        e.target.checked

                                      );


                                      updateLevel4Task(

                                        "helpMember",

                                        e.target.checked

                                      );

                                    }

                                  }

                                }

                              />

                            </>

                          )

                          :

                          (

                            <span
                              className="tick-icon"
                            >

                              {

                                task.completed

                                ?

                                "✅"

                                :

                                "⏳"

                              }

                            </span>

                          )

                        }


                        <span>

                          {

                            task.label

                          }

                        </span>


                      </div>


                      {/* =================================================
                          STATUS
                      ================================================= */}

                      {

                        task.completed

                        ?

                        (

                          <span
                            className="complete-badge"
                          >

                            Completed

                          </span>

                        )

                        :

                        (

                          <span
                            className="pending-badge"
                          >

                            {

                              task.manual &&
                              !level3Completed

                              ?

                              "Locked"

                              :

                              "Pending"

                            }

                          </span>

                        )

                      }


                    </div>

                  )

                )

              }


            </div>


            {/* =================================================
                LEVEL 3 LOCK MESSAGE
            ================================================= */}

            {

              !level3Completed && (

                <div
                  className="level3-lock-message"
                >

                  🔒 Complete Level 3 to unlock
                  Level 4 manual tasks.

                </div>

              )

            }


            {/* =================================================
                METRIC PROGRESS
            ================================================= */}

            <div
              className="metric-progress-card"
            >


              <div
                className="metric-header"
              >

                <h3>

                  Improve
                  3 Metrics

                </h3>


                <span>

                  {

                    metricsProgress

                  }

                  /3

                </span>

              </div>


              <div
                className="metric-track"
              >

                <div

                  className="metric-fill"

                  style={{

                    width:

                      `${
                        (
                          metricsProgress /
                          3
                        ) * 100
                      }%`

                  }}

                />

              </div>


              <p>

                Improve{" "}

                {

                  Math.max(

                    0,

                    3 -
                    metricsProgress

                  )

                }{" "}

                more metric(s)
                to complete
                this requirement.

              </p>


            </div>


            {/* =================================================
                OVERALL PROGRESS
            ================================================= */}

            <div
              className="progress-section"
            >


              <div
                className="progress-track"
              >

                <div

                  className="progress-fill"

                  style={{

                    width:

                      `${progressPercent}%`

                  }}

                />

              </div>


              <div
                className="progress-footer"
              >

                <span>

                  {

                    completedTasks

                  }

                  /

                  {

                    tasks.length

                  }

                  {" "}

                  Complete

                </span>


                <span>

                  {

                    progressPercent

                  }%

                </span>

              </div>


            </div>


          </div>


        </div>


        {/* =================================================
            RIGHT SIDE
        ================================================= */}

        <div
          className="thrive-right"
        >


          {/* DAILY STREAK */}

          <div
            className="stat-card"
          >

            <div
              className="stat-icon"
            >

              🔥

            </div>


            <h3>

              {dailyStreak}

            </h3>


            <p>

              Daily Streak

            </p>

          </div>


          {/* CURRENT LEVEL */}

          <div
            className="stat-card"
          >

            <div
              className="stat-icon"
            >

              💎

            </div>


            <h3>

              {currentLevel}

            </h3>


            <p>

              Current Level

            </p>

          </div>


          {/* THRIVE PROGRESS */}

          <div
            className="stat-card"
          >

            <div
              className="stat-icon"
            >

              📈

            </div>


            <h3>

              {progressPercent}%

            </h3>


            <p>

              Thrive Progress

            </p>

          </div>


        </div>


      </section>


      {/* =================================================
          PERSONAL BESTS
      ================================================= */}

      <section
        className="records-section"
      >


        <div
          className="section-header"
        >

          <h2>

            Personal Bests

          </h2>


          <p>

            Your highest recorded
            vision and cognitive
            performance.

          </p>

        </div>


        <div
          className="records-grid"
        >

          {

            personalRecords.map(

              (
                item,
                index
              ) => (

                <div

                  key={index}

                  className="record-card"

                >

                  <h3>

                    {

                      item.title

                    }

                  </h3>


                  <h1>

                    {

                      item.value

                    }

                  </h1>


                  <span>

                    Personal Best

                  </span>

                </div>

              )

            )

          }

        </div>


      </section>


      {/* =================================================
          TODAY'S VISION ROUTINE
      ================================================= */}

      <section
        className="routine-section"
      >


        <div
          className="section-header"
        >

          <h2>

            Today's Vision Routine

          </h2>


          <p>

            Complete your daily
            vision maintenance
            routine to stay at
            your peak.

          </p>

        </div>


        <div
          className="routine-card"
        >


          {

            routineTasks.map(

              (
                item,
                index
              ) => (

                <div

                  key={index}

                  className="routine-item"

                >


                  <div
                    className="routine-left"
                  >


                    <input

                      type="checkbox"

                      checked={
                        item.checked
                      }

                      onChange={

                        (e) => {

                          item.setter(

                            e.target.checked

                          );


                          updateLevel4Task(

                            item.field,

                            e.target.checked

                          );

                        }

                      }

                    />


                    <div>

                      <h3>

                        {

                          item.label

                        }

                      </h3>


                      <p>

                        Complete today's
                        activity.

                      </p>

                    </div>


                  </div>


                  <span

                    className={

                      item.checked

                        ?

                        "complete-badge"

                        :

                        "pending-badge"

                    }

                  >

                    {

                      item.checked

                        ?

                        "Done"

                        :

                        "Pending"

                    }

                  </span>


                </div>

              )

            )

          }


          {/* =================================================
              ROUTINE PROGRESS
          ================================================= */}

          <div
            className="routine-progress"
          >


            <div
              className="routine-track"
            >

              <div

                className="routine-fill"

                style={{

                  width:

                    `${
                      (
                        completedRoutine /
                        4
                      ) * 100
                    }%`

                }}

              />

            </div>


            <div
              className="routine-footer"
            >

              <span>

                {

                  completedRoutine

                }

                /4 Completed

              </span>


              <span>

                {

                  Math.round(

                    (

                      completedRoutine /
                      4

                    ) * 100

                  )

                }%

              </span>

            </div>


          </div>


        </div>


      </section>


      {/* =================================================
          PERFORMANCE GRAPHS
      ================================================= */}

      <PerformanceGraphs
        history={history}
      />


    </div>

  );

};


export default Thrive;